import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { newCell } from "@/lib/designer";

export const DRAFT_KEY = "lumina_draft";
const LIMIT = 50;

function reducer(s, a) {
  const resolve = (next) => (typeof next === "function" ? next(s.present) : next);
  switch (a.type) {
    case "commit": return { past: [...s.past.slice(-LIMIT), s.present], present: resolve(a.next), future: [] };
    case "preview": return { ...s, present: resolve(a.next) };
    case "push": return { ...s, past: [...s.past.slice(-LIMIT), a.snapshot], future: [] };
    case "undo": return s.past.length ? { past: s.past.slice(0, -1), present: s.past[s.past.length - 1], future: [s.present, ...s.future] } : s;
    case "redo": return s.future.length ? { past: [...s.past, s.present], present: s.future[0], future: s.future.slice(1) } : s;
    case "load": return { past: [], present: a.next, future: [] };
    default: return s;
  }
}

// override: explicit talla/orientación from the URL, applied on top of a loaded draft
export const useDesigner = ({ slug, orientation, designParam, override }) => {
  const [h, dispatch] = useReducer(reducer, { past: [], present: { slug, orientation, layout: 1, cells: [newCell()] }, future: [] });
  const [design, setDesignState] = useState(null);
  const [saving, setSaving] = useState("idle");
  const [loaded, setLoaded] = useState(false);
  const dirty = useRef(false);
  const designRef = useRef(null);
  const state = h.present;

  useEffect(() => {
    const id = designParam || localStorage.getItem(DRAFT_KEY);
    if (!id) { setLoaded(true); return; }
    api.get(`/designs/${id}`).then(({ data }) => {
      designRef.current = data; setDesignState(data);
      if (!designParam) localStorage.setItem(DRAFT_KEY, data.id);
      const next = { slug: override.slug || data.product_slug, orientation: override.orientation || data.orientation, layout: data.layout, cells: data.cells };
      if (next.slug !== data.product_slug || next.orientation !== data.orientation) dirty.current = true;
      dispatch({ type: "load", next });
    }).catch(() => { if (!designParam) localStorage.removeItem(DRAFT_KEY); }).finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mark = (a) => { dirty.current = true; dispatch(a); };
  const commit = useCallback((next) => mark({ type: "commit", next }), []);
  const preview = useCallback((next) => mark({ type: "preview", next }), []);
  const pushHistory = useCallback((snapshot) => dispatch({ type: "push", snapshot }), []);

  const save = useCallback(async (st, force = false) => {
    if (!force && !dirty.current && designRef.current) return designRef.current;
    setSaving("saving");
    try {
      const cur = designRef.current;
      const body = { product_slug: st.slug, orientation: st.orientation, layout: st.layout, cells: st.cells };
      const { data } = cur ? await api.put(`/designs/${cur.id}`, body) : await api.post("/designs", body);
      designRef.current = data; setDesignState(data);
      if (!designParam) localStorage.setItem(DRAFT_KEY, data.id);
      dirty.current = false; setSaving("saved");
      return data;
    } catch (e) { setSaving("error"); throw new Error(errMsg(e)); }
  }, [designParam]);

  useEffect(() => {
    if (!loaded || !dirty.current) return;
    const t = setTimeout(() => save(state).catch(() => {}), 1200);
    return () => clearTimeout(t);
  }, [state, loaded, save]);

  return {
    state, commit, preview, pushHistory,
    undo: () => mark({ type: "undo" }), redo: () => mark({ type: "redo" }),
    canUndo: h.past.length > 0, canRedo: h.future.length > 0,
    design, setDesign: (d) => { designRef.current = d; setDesignState(d); }, save, saving, loaded,
  };
};
